import React, { useRef, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
    signatureContainer: {
        border: '2px dashed #ccc',
        borderRadius: '8px',
        padding: theme.spacing(2),
        backgroundColor: '#fafafa',
        marginTop: theme.spacing(2),
    },
    canvasWrapper: {
        border: '1px solid #ddd',
        borderRadius: '4px',
        backgroundColor: '#fff',
        overflow: 'hidden',
        touchAction: 'none',
    },
    signatureCanvas: {
        width: '100%',
        height: '200px',
        cursor: 'crosshair',
        display: 'block',
    },
    buttonContainer: {
        marginTop: theme.spacing(2),
        display: 'flex',
        gap: theme.spacing(1),
        justifyContent: 'flex-end',
    },
    title: {
        marginBottom: theme.spacing(2),
        color: theme.palette.text.secondary,
        fontSize: '14px',
        fontWeight: 500,
    },
    nameField: {
        marginBottom: theme.spacing(2),
    }
}));

function SignatureDrawingPad({ onSave, loading }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [isEmpty, setIsEmpty] = useState(true);
    const [context, setContext] = useState(null);
    const [personName, setPersonName] = useState('');

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const applyDrawingProps = (ctx) => {
            ctx.strokeStyle = '#000';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
        };

        const initCanvas = () => {
            const width = canvas.offsetWidth || 600;
            canvas.width = width;
            canvas.height = 200;
            const ctx = canvas.getContext('2d');
            applyDrawingProps(ctx);
            setContext(ctx);
        };

        initCanvas();

        // Handle window resize
        const handleResize = () => {
            const ctx = canvas.getContext('2d');
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            canvas.width = canvas.offsetWidth || 600;
            canvas.height = 200;
            ctx.putImageData(imageData, 0, 0);
            applyDrawingProps(ctx);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getCoordinates = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        if (e.touches && e.touches.length > 0) {
            // Touch event
            return {
                x: e.touches[0].clientX - rect.left,
                y: e.touches[0].clientY - rect.top
            };
        } else {
            // Mouse event
            return {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            };
        }
    };

    const startDrawing = (e) => {
        e.preventDefault();
        if (!context) return;

        setIsDrawing(true);
        setIsEmpty(false);

        const { x, y } = getCoordinates(e);
        context.beginPath();
        context.moveTo(x, y);
    };

    const draw = (e) => {
        e.preventDefault();
        if (!isDrawing || !context) return;

        const { x, y } = getCoordinates(e);
        context.lineTo(x, y);
        context.stroke();
    };

    const stopDrawing = (e) => {
        e.preventDefault();
        if (!context) return;

        setIsDrawing(false);
        context.closePath();
    };

    const handleClear = () => {
        const canvas = canvasRef.current;
        if (!context || !canvas) return;

        context.clearRect(0, 0, canvas.width, canvas.height);
        setIsEmpty(true);
    };

    const handleSave = () => {
        const canvas = canvasRef.current;
        if (!canvas || isEmpty) return;

        // Use a temporary canvas with white background to avoid black rectangles
        // when the image is rendered without alpha channel support
        const flatCanvas = document.createElement('canvas');
        flatCanvas.width = canvas.width;
        flatCanvas.height = canvas.height;
        const flatCtx = flatCanvas.getContext('2d');
        flatCtx.fillStyle = '#ffffff';
        flatCtx.fillRect(0, 0, flatCanvas.width, flatCanvas.height);
        flatCtx.drawImage(canvas, 0, 0);

        const dataURL = flatCanvas.toDataURL('image/png');
        onSave(dataURL, personName);
    };

    return (
        <Paper className={classes.signatureContainer} elevation={0}>
            <div className={classes.title}>
                {t('services.visits.payments.drawSignature') || 'Firma persona responsable'}
            </div>
            <TextField
                fullWidth
                label={t('services.visits.payments.personAccountable') || 'Nombre de quien firma'}
                value={personName}
                onChange={(e) => setPersonName(e.target.value)}
                disabled={loading}
                className={classes.nameField}
                variant="outlined"
                size="small"
            />
            <div className={classes.canvasWrapper}>
                <canvas
                    ref={canvasRef}
                    className={classes.signatureCanvas}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                />
            </div>
            <Box className={classes.buttonContainer}>
                <Button
                    variant="outlined"
                    color="default"
                    onClick={handleClear}
                    disabled={isEmpty || loading}
                >
                    {t('services.visits.payments.clearSignature') || 'Limpiar'}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={isEmpty || loading}
                >
                    {t('services.visits.payments.saveSignature') || 'Guardar Firma'}
                </Button>
            </Box>
        </Paper>
    );
}

export default SignatureDrawingPad;
